import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  CheckCircle2,
  ShieldCheck,
  Search,
  FileText,
  Hammer,
  Building2,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { ServiceAreasSection } from "@/components/CrossLinks";

export default function Commercial() {
  const schema = [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Home",
          item: "https://www.saskroofingcompany.com/",
        },
        {
          "@type": "ListItem",
          position: 2,
          name: "Services",
          item: "https://www.saskroofingcompany.com/services",
        },
        {
          "@type": "ListItem",
          position: 3,
          name: "Commercial Roofing",
          item: "https://www.saskroofingcompany.com/services/commercial",
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "Service",
      "@id": "https://www.saskroofingcompany.com/services/commercial#service",
      serviceType: "Commercial Roofing",
      name: "Commercial Roofing",
      description:
        "Commercial roofing contractor in Saskatchewan. We install and repair TPO, EPDM, and modified bitumen flat roofs for businesses.",
      url: "https://www.saskroofingcompany.com/services/commercial",
      provider: {
        "@id": "https://www.saskroofingcompany.com/#localbusiness",
        "@type": "RoofingContractor",
        name: "Sask Roofing Company",
        url: "https://www.saskroofingcompany.com/",
        telephone: "+1-639-739-7969",
      },
      areaServed: [
        {
          "@type": "City",
          name: "Saskatoon",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Regina",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Moose Jaw",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Prince Albert",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Swift Current",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Yorkton",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Estevan",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
        {
          "@type": "City",
          name: "Weyburn",
          containedInPlace: { "@type": "State", name: "Saskatchewan" },
        },
      ],
      offers: {
        "@type": "AggregateOffer",
        priceCurrency: "CAD",
        lowPrice: "8000",
        highPrice: "80000",
      },
      availableChannel: {
        "@type": "ServiceChannel",
        serviceUrl: "https://www.saskroofingcompany.com/contact",
        servicePhone: {
          "@type": "ContactPoint",
          telephone: "+1-639-739-7969",
          contactType: "customer service",
          areaServed: "CA-SK",
          availableLanguage: "English",
        },
      },
      termsOfService: "https://www.saskroofingcompany.com/about#warranty",
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "4.9",
        reviewCount: "87",
        bestRating: "5",
        worstRating: "1",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: [
        {
          "@type": "Question",
          name: "What is the best flat roofing material for Saskatchewan?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "TPO and EPDM are both excellent choices. TPO offers great energy efficiency, while EPDM remains highly flexible in extreme cold temperatures.",
          },
        },
        {
          "@type": "Question",
          name: "Do you offer commercial roof maintenance programs?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Yes, we offer preventative maintenance plans to clear debris from drains, inspect seams, and extend the lifespan of your commercial roof.",
          },
        },
        {
          "@type": "Question",
          name: "Can you repair my commercial roof without replacing it?",
          acceptedAnswer: {
            "@type": "Answer",
            text: "Often, yes. If the insulation isn't saturated and the membrane is generally in good condition, we can perform targeted repairs or apply a restorative roof coating.",
          },
        },
      ],
    },
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title="Commercial Roofing Saskatchewan | Sask Roofing"
        description="Expert commercial roofing in Saskatchewan. We specialize in TPO, EPDM, and flat roof installations for businesses, built to withstand the prairie climate."
        schema={schema}
      />

      {/* Hero */}
      <section className="relative py-32 text-primary-foreground overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b36d45df-4111-4a48-a8f9-5b1eb8b63f52.png&output=webp&q=60"
            alt="Commercial Flat Roofing Installation"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent"></div>
        </div>
        <div className="container relative px-4 z-10">
          <div className="max-w-3xl space-y-6">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-white drop-shadow-md">
              Commercial & Light Industrial Roofing
            </h1>
            <p className="text-xl text-white/90 drop-shadow-sm font-medium">
              Protect your business and inventory with a reliable commercial
              roof. We install, repair, and maintain flat roofing systems
              engineered for Saskatchewan's extreme climate.
              <br />
              <br />
              <strong>
                Looking for full B2B commercial services, net terms, and
                large-scale projects?{" "}
              </strong>
              <Link
                to="/commercial"
                className="underline text-accent hover:text-accent/80 transition-colors"
              >
                Visit our dedicated Commercial B2B page.
              </Link>
            </p>
            <div className="pt-4 flex flex-col sm:flex-row gap-4">
              <Button
                size="lg"
                className="bg-accent text-accent-foreground hover:bg-accent/90 shadow-lg"
                asChild
              >
                <Link to="/contact">Request a Commercial Quote</Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="bg-transparent text-white border-white hover:bg-white/10 shadow-lg"
                asChild
              >
                <a href="tel:6397397969">Call Now (639) 739-7969</a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* When you need it & Warning Signs */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">
                When You Need Commercial Roofing
              </h2>
              <p className="text-lg text-muted-foreground leading-relaxed">
                A compromised commercial roof threatens your inventory,
                equipment, and daily operations. You need a commercial roofing
                expert when your flat roof is experiencing ponding water, open
                seams, or membrane blistering. We also provide new installations
                for commercial construction projects.
              </p>

              <div className="space-y-4 my-8">
                <h3 className="text-xl font-bold text-foreground flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-accent" />
                  Signs Your Flat Roof Needs Attention
                </h3>
                <ul className="space-y-4">
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      01
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Ponding Water
                      </strong>
                      <span className="text-muted-foreground">
                        Water that stays on the roof 48 hours after rain
                        indicates poor drainage or sagging insulation.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      02
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Open Seams
                      </strong>
                      <span className="text-muted-foreground">
                        Adhesives break down over time, allowing water to
                        penetrate between membrane panels.
                      </span>
                    </div>
                  </li>
                  <li className="flex gap-4">
                    <span className="text-3xl font-bold text-primary/20 leading-none">
                      03
                    </span>
                    <div>
                      <strong className="text-foreground block">
                        Blistering or Alligatoring
                      </strong>
                      <span className="text-muted-foreground">
                        Bubbles in the membrane or a cracked, dry surface mean
                        the material is failing.
                      </span>
                    </div>
                  </li>
                </ul>
              </div>
            </div>

            <div className="space-y-8">
              <div className="bg-muted/30 p-8 rounded-xl border">
                <h3 className="text-2xl font-bold mb-6">
                  Why It Suits Saskatchewan
                </h3>
                <ul className="space-y-4">
                  {[
                    "Membranes chosen for flexibility in -40°C weather to prevent winter cracking.",
                    "Proper insulation tapering to ensure drainage during heavy spring snowmelt.",
                    "High wind-uplift resistance to withstand open prairie gusts.",
                    "Energy-efficient options (like white TPO) to reduce summer cooling costs.",
                  ].map((item, i) => (
                    <li key={i} className="flex gap-3 text-muted-foreground">
                      <CheckCircle2 className="w-6 h-6 text-primary shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="relative rounded-2xl overflow-hidden shadow-xl h-64">
                <img
                  src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b36d45df-4111-4a48-a8f9-5b1eb8b63f52.png&output=webp&q=60"
                  alt="Heat welding TPO membrane seams"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Materials We Use */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="max-w-3xl mx-auto space-y-6 text-center">
            <h2 className="text-3xl font-bold">
              Materials We Use for Commercial Roofing
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              We install industry-leading single-ply membrane systems, primarily
              TPO (Thermoplastic Polyolefin) and EPDM (Ethylene Propylene Diene
              Monomer). TPO is highly reflective, energy-efficient, and features
              incredibly strong heat-welded seams. EPDM is a durable rubber
              roofing system known for its exceptional flexibility in freezing
              temperatures. For heavy-traffic roofs, we also offer multi-ply
              Modified Bitumen systems. We pair these membranes with
              high-R-value rigid foam insulation (Polyiso) tapered perfectly to
              guarantee positive drainage.
            </p>
          </div>
        </div>
      </section>

      {/* Cost & Comparison Table */}
      <section className="py-20 bg-background">
        <div className="container px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center mb-16">
            <div className="space-y-6">
              <h2 className="text-3xl font-bold">What It Costs</h2>

              <div className="bg-primary/5 border border-primary/20 p-6 rounded-xl">
                <h3 className="font-bold text-xl mb-2 text-foreground">
                  Estimated Cost: $8 – $16 per sq. ft.
                </h3>
                <p className="text-sm text-muted-foreground italic">
                  *This is a rough estimate for full flat roof replacement. Cost
                  depends heavily on insulation requirements, tear-off depth,
                  and number of penetrations.
                </p>
              </div>

              <p className="text-lg text-muted-foreground leading-relaxed">
                Commercial roofing costs vary widely. A simple layover on a
                clean deck will be on the lower end, while a full tear-off
                requiring new tapered insulation to fix drainage issues will be
                on the higher end. We provide detailed, honest bids that outline
                all materials, labor, and safety equipment, ensuring absolutely
                no surprises. For general pricing information, see our{" "}
                <Link
                  to="/roofing-cost-guide-saskatchewan"
                  className="text-accent hover:underline"
                >
                  Saskatchewan Roofing Cost Guide
                </Link>
                .
              </p>
            </div>

            <div className="relative rounded-2xl overflow-hidden shadow-xl h-80">
              <img
                src="https://wsrv.nl/?url=https://vibe.filesafe.space/1783106672919603654/assets/b36d45df-4111-4a48-a8f9-5b1eb8b63f52.png&output=webp&q=60"
                alt="Completed Commercial Flat Roof"
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border bg-background shadow-sm max-w-4xl mx-auto">
            <h3 className="text-xl font-bold p-4 bg-muted/30 border-b">
              Flat Roofing Systems Compared
            </h3>
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-primary text-primary-foreground">
                  <th className="p-4 font-bold border-b border-primary-foreground/20">
                    Feature
                  </th>
                  <th className="p-4 font-bold border-b border-primary-foreground/20">
                    TPO (White)
                  </th>
                  <th className="p-4 font-bold border-b border-primary-foreground/20">
                    EPDM (Black)
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="p-4 font-medium text-foreground">
                    Seam Strength
                  </td>
                  <td className="p-4 text-muted-foreground font-bold text-primary">
                    Exceptional (Heat Welded)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Good (Taped/Adhered)
                  </td>
                </tr>
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="p-4 font-medium text-foreground">
                    Energy Efficiency
                  </td>
                  <td className="p-4 text-muted-foreground font-bold text-primary">
                    High (Reflects UV)
                  </td>
                  <td className="p-4 text-muted-foreground">
                    Low (Absorbs Heat)
                  </td>
                </tr>
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="p-4 font-medium text-foreground">
                    Cold Weather Flexibility
                  </td>
                  <td className="p-4 text-muted-foreground">Good</td>
                  <td className="p-4 text-muted-foreground font-bold text-primary">
                    Exceptional
                  </td>
                </tr>
                <tr className="hover:bg-muted/50 transition-colors">
                  <td className="p-4 font-medium text-foreground">
                    Puncture Resistance
                  </td>
                  <td className="p-4 text-muted-foreground">High</td>
                  <td className="p-4 text-muted-foreground">Moderate</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* Process Timeline */}
      <section className="py-20 bg-muted/30 border-y">
        <div className="container px-4">
          <div className="text-center max-w-3xl mx-auto mb-16 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground">
              Our Commercial Process
            </h2>
            <p className="text-lg text-muted-foreground">
              Professional execution designed to minimize disruption to your
              business operations.
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8 relative">
            <div className="hidden md:block absolute top-8 left-[10%] right-[10%] h-0.5 bg-border -z-10"></div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <Search className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">1. Core Testing</h3>
              <p className="text-muted-foreground">
                We perform core cuts to evaluate existing insulation layers and
                check for trapped moisture.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <FileText className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">2. System Design</h3>
              <p className="text-muted-foreground">
                We design an insulation and membrane package that meets building
                codes and solves drainage issues.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <Hammer className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">3. Safe Installation</h3>
              <p className="text-muted-foreground">
                Our certified crews install the system efficiently, adhering to
                strict commercial safety standards.
              </p>
            </div>

            <div className="relative space-y-4 bg-muted/30 pt-2 text-center md:text-left">
              <div className="w-16 h-16 bg-primary text-primary-foreground rounded-full flex items-center justify-center border-4 border-muted/30 mx-auto md:mx-0 shadow-sm">
                <ShieldCheck className="w-6 h-6 text-accent" />
              </div>
              <h3 className="text-xl font-bold">4. Quality Control</h3>
              <p className="text-muted-foreground">
                Every seam is probed and verified. We provide comprehensive
                manufacturer warranties upon completion.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <ServiceAreasSection
        service="Commercial Roofing"
        serviceSlug="commercial"
      />

      {/* FAQ */}
      <section className="py-20 bg-background border-y">
        <div className="container px-4 max-w-4xl">
          <h2 className="text-3xl font-bold text-center mb-12">
            Commercial Roofing FAQ
          </h2>
          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="item-1">
              <AccordionTrigger className="text-lg font-medium text-left">
                What is the best flat roofing material for Saskatchewan?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                TPO and EPDM are both excellent choices. TPO offers great energy
                efficiency, while EPDM remains highly flexible in extreme cold
                temperatures.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2">
              <AccordionTrigger className="text-lg font-medium text-left">
                Do you offer commercial roof maintenance programs?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Yes, we offer preventative maintenance plans to clear debris
                from drains, inspect seams, and extend the lifespan of your
                commercial roof.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3">
              <AccordionTrigger className="text-lg font-medium text-left">
                Can you repair my commercial roof without replacing it?
              </AccordionTrigger>
              <AccordionContent className="text-muted-foreground text-base">
                Often, yes. If the insulation isn't saturated and the membrane
                is generally in good condition, we can perform targeted repairs
                or apply a restorative roof coating.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </section>

      {/* Cross Links & CTA Banner */}
      <section className="py-20 bg-primary text-primary-foreground text-center">
        <div className="container px-4 max-w-4xl space-y-8">
          <h2 className="text-3xl md:text-4xl font-bold">
            Secure Your Business Assets
          </h2>
          <p className="text-xl text-primary-foreground/90 leading-relaxed">
            We serve commercial clients across Saskatoon, Regina, Moose Jaw,
            Prince Albert, Swift Current, Yorkton, Estevan, and Weyburn.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 pt-4">
            <Button
              size="lg"
              className="bg-accent text-accent-foreground hover:bg-accent/90 text-lg px-8 py-6 h-auto"
              asChild
            >
              <Link to="/contact">Request a Commercial Bid</Link>
            </Button>
            <Button
              size="lg"
              variant="outline"
              className="text-lg px-8 py-6 h-auto bg-transparent border-white text-white hover:bg-white/10"
              asChild
            >
              <Link to="/process">See Our Process</Link>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}
